import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  targetAlpha: number;
  twinkleSpeed: number;
  color: string;
  isRespawning: boolean;
}

interface ParticlesBackgroundProps {
  color?: string;
  glowColor?: string;
  /** Whether to show the classic spinning mesh background from the previous version */
  showMesh?: boolean;
}

export function ParticlesBackground({ 
  color = 'rgba(255, 255, 255, 0.9)',  // White stars
  glowColor = 'rgba(100, 180, 255, 0.8)', // Blue glow
  showMesh = true
}: ParticlesBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Use classic purple if in mesh mode, otherwise use the provided color
  const effectiveColor = showMesh ? 'rgba(147, 51, 234, 0.9)' : color;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    let time = 0;
    let lastTime = 0;
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticles = () => {
      // Classic mode is slightly less dense (1/15000) than Cosmic mode (1/10000)
      const density = showMesh ? 15000 : 10000;
      const particleCount = Math.floor((window.innerWidth * window.innerHeight) / density);
      particles = [];
      
      for (let i = 0; i < particleCount; i++) {
        // Classic mode velocity was 0.2, Cosmic is 0.15 (slower to let flow field take over)
        const speedScale = showMesh ? 0.2 : 0.15;
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * speedScale,
          vy: (Math.random() - 0.5) * speedScale,
          // Classic stars were slightly larger (up to 2.5px)
          size: showMesh ? (Math.random() * 2 + 0.5) : (Math.random() * 1.8 + 0.3),
          alpha: Math.random() * 0.5 + 0.2,
          targetAlpha: Math.random() * 0.7 + 0.3,
          twinkleSpeed: Math.random() * 0.02 + 0.01,
          color: effectiveColor,
          isRespawning: false,
        });
      }
    };

    const draw = (timestamp: number = 0) => {
      if (!ctx || !canvas) return;
      
      // Frame-independent timing
      const deltaTime = lastTime ? Math.min(timestamp - lastTime, 100) : 16.67;
      lastTime = timestamp;
      const dtScale = deltaTime / 16.67; // Normalize to 60fps
      time += 0.005 * dtScale;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const w = canvas.width;
      const h = canvas.height;
      
      particles.forEach((p) => {
        if (!showMesh) {
          // Flow Field - gentle oceanic currents (Cosmic only)
          const flowScale = 0.001;
          const flowX = Math.sin(p.y * flowScale + time * 0.3) * 0.04;
          const flowY = Math.cos(p.x * flowScale + time * 0.3) * 0.04;
          p.vx += flowX * dtScale;
          p.vy += flowY * dtScale;
          
          // Random drift - subtle bubbles effect (Cosmic only)
          p.vx += (Math.random() - 0.5) * 0.008 * dtScale;
          p.vy += (Math.random() - 0.5) * 0.008 * dtScale;
        }

        // Anti-clumping: when a star is fully faded out, relocate it
        // This makes "recycling" look like natural twinkling
        if (p.alpha < 0.05 && p.targetAlpha === 0) {
          p.x = Math.random() * w;
          p.y = Math.random() * h;
          // Re-randomize velocity based on mode
          // Classic: 0.2 (per frame at 60fps), Cosmic: 0.15 (slower base)
          const speedScale = showMesh ? 0.2 : 0.15;
          p.vx = (Math.random() - 0.5) * speedScale;
          p.vy = (Math.random() - 0.5) * speedScale;
          p.targetAlpha = Math.random() * 0.7 + 0.3;
        }

        if (!showMesh) {
          // Velocity cap and Friction (Cosmic only)
          const maxVelocity = 0.5;
          const velocity = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
          if (velocity > maxVelocity) {
            p.vx *= maxVelocity / velocity;
            p.vy *= maxVelocity / velocity;
          }
          
          p.vx *= Math.pow(0.985, dtScale);
          p.vy *= Math.pow(0.985, dtScale);
        }
        
        // Movement
        p.x += p.vx * dtScale;
        p.y += p.vy * dtScale;

        // Wrap around screen
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        // Twinkle effect
        if (Math.abs(p.alpha - p.targetAlpha) < 0.01) {
          // 5% chance to "die" (fade out and relocate)
          if (Math.random() < 0.05) {
            p.targetAlpha = 0;
          } else {
            p.targetAlpha = Math.random() * 0.7 + 0.3;
          }
        }
        p.alpha += (p.targetAlpha - p.alpha) * (p.twinkleSpeed * 0.5) * dtScale;

        // Draw particle
        if (!showMesh) {
          // Glow effect (Cosmic only)
          ctx.shadowBlur = p.size * 6;
          ctx.shadowColor = glowColor;
        }
        
        // Particle core color
        ctx.fillStyle = effectiveColor.replace(/[\d.]+\)$/, p.alpha.toString() + ')');

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.shadowBlur = 0; // Reset for next particle
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    createParticles();
    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [effectiveColor, glowColor, showMesh]);

  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
      {/* Deep, rich animated gradient mesh - only in classic mode */}
      {showMesh && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_#1a103c_0%,_#0a0a0f_100%)]"></div>
          <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] animate-spin-slow opacity-30 bg-[conic-gradient(from_0deg_at_50%_50%,_#0a0a0f_0deg,_#2e1065_60deg,_#0a0a0f_120deg,_#1e1b4b_180deg,_#0a0a0f_240deg,_#4c1d95_300deg,_#0a0a0f_360deg)] mix-blend-screen blur-3xl"></div>
        </>
      )}
      
      {/* Canvas for particles */}
      <canvas 
        ref={canvasRef} 
        className={`absolute inset-0 w-full h-full ${showMesh ? 'opacity-60' : 'opacity-90'}`}
      />
    </div>
  );
}
